/**
 * Amizek Analytics & Monitoring
 * Google Analytics 4 integration and comprehensive monitoring
 */

class AmizekAnalytics {
  constructor() {
    this.config = {
      measurementId: 'G-5DQ5SR89MX', // Confirmed User ID
      tikTokPixelId: 'CV1BKJBC77U759IQA66G',
      metaPixelId: '10027409053954790',
      debugMode: false,
      enhancedEcommerce: true,
      customDimensions: {
        userType: 'custom_1',
        pageType: 'custom_2',
        productCategory: 'custom_3'
      }
    };
    this.init();
  }

  init() {
    this.loadGoogleAnalytics();
    this.loadTikTokPixel();
    this.loadMetaPixel();
    this.setupEnhancedEcommerce();
    this.setupCustomEvents();
    this.setupPerformanceMonitoring();
    this.setupUserBehaviorTracking();
    this.setupConversionTracking();
    if (window.amizekAdminEnabled) {
      this.setupRealTimeMonitoring();
    }
  }

  loadGoogleAnalytics() {
    // Load Google Analytics 4
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${this.config.measurementId}`;
    document.head.appendChild(script);

    // Initialize gtag
    window.dataLayer = window.dataLayer || [];
    function gtag() { dataLayer.push(arguments); }
    window.gtag = gtag;
    gtag('js', new Date());
    gtag('config', this.config.measurementId, {
      send_page_view: true,
      custom_map: this.config.customDimensions,
      enhanced_ecommerce: this.config.enhancedEcommerce
    });

    // Set up custom dimensions
    this.setupCustomDimensions();
  }

  loadTikTokPixel() {
    // Load TikTok Pixel
    (function (w, d, t) {
      w.TiktokAnalyticsObject = t; var ttq = w[t] = w[t] || [];
      ttq.methods = ["page", "track", "identify", "instances", "debug", "on", "off", "once", "ready", "alias", "group", "enableCookie", "disableCookie"],
        ttq.setAndDefer = function (t, e) { t[e] = function () { t.push([e].concat(Array.prototype.slice.call(arguments, 0))) } };
      for (var i = 0; i < ttq.methods.length; i++)ttq.setAndDefer(ttq, ttq.methods[i]);
      ttq.instance = function (t) { for (var e = ttq._i[t] || [], n = 0; n < ttq.methods.length; n++)ttq.setAndDefer(e, ttq.methods[n]); return e }, ttq.load = function (e, n) { var i = "https://analytics.tiktok.com/i18n/pixel/events.js"; ttq._i = ttq._i || {}, ttq._i[e] = [], ttq._i[e]._u = i, ttq._t = ttq._t || {}, ttq._t[e] = +new Date, ttq._o = ttq._o || {}, ttq._o[e] = n || {}; var o = document.createElement("script"); o.type = "text/javascript", o.async = !0, o.src = i + "?sdkid=" + e + "&lib=" + t; var a = document.getElementsByTagName("script")[0]; a.parentNode.insertBefore(o, a) };
    }(window, document, 'ttq'));

    if (window.ttq) {
      window.ttq.load(this.config.tikTokPixelId);
      window.ttq.page();
    }
  }

  loadMetaPixel() {
    // Load Meta (Facebook) Pixel
    !function (f, b, e, v, n, t, s) {
      if (f.fbq) return; n = f.fbq = function () {
        n.callMethod ?
          n.callMethod.apply(n, arguments) : n.queue.push(arguments)
      };
      if (!f._fbq) f._fbq = n; n.push = n; n.loaded = !0; n.version = '2.0';
      n.queue = []; t = b.createElement(e); t.async = !0;
      t.src = v; s = b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t, s)
    }(window, document, 'script',
      'https://connect.facebook.net/en_US/fbevents.js');

    if (window.fbq) {
      window.fbq('init', this.config.metaPixelId);
      window.fbq('track', 'PageView');
    }
  }

  setupCustomDimensions() {
    // Set custom dimensions
    if (typeof gtag === 'function') {
      gtag('config', this.config.measurementId, {
        custom_map: {
          'custom_1': 'user_type',
          'custom_2': 'page_type',
          'custom_3': 'product_category'
        }
      });

      // Set user type
      const userType = this.detectUserType();
      gtag('event', 'user_type_set', {
        user_type: userType
      });
    }
  }

  detectUserType() {
    // Detect user type based on behavior
    const isReturning = localStorage.getItem('amizek_visitor') !== null;
    const hasPurchased = localStorage.getItem('amizek_purchased') === 'true';

    if (hasPurchased) return 'customer';
    if (isReturning) return 'returning_visitor';
    return 'new_visitor';
  }

  setupEnhancedEcommerce() {
    // Enhanced Ecommerce tracking
    this.trackProductViews();
    this.trackAddToCart();
    this.trackRemoveFromCart();
    this.trackCheckout();
    this.trackPurchases();
    this.trackRefunds();
  }

  trackProductViews() {
    // Track product page views
    if (window.location.pathname.includes('/products/')) {
      const productData = this.extractProductData();
      if (productData) {
        gtag('event', 'view_item', {
          currency: 'GBP',
          value: productData.price,
          items: [{
            item_id: productData.id,
            item_name: productData.name,
            item_category: productData.category,
            item_brand: productData.brand,
            price: productData.price,
            quantity: 1
          }]
        });
      }
    }
  }

  trackAddToCart() {
    // Track add to cart events
    const addToCartButtons = document.querySelectorAll('[data-add-to-cart], .add-to-cart, button[type="submit"]');
    addToCartButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        const productData = this.extractProductData();
        if (productData) {
          gtag('event', 'add_to_cart', {
            currency: 'GBP',
            value: productData.price,
            items: [{
              item_id: productData.id,
              item_name: productData.name,
              item_category: productData.category,
              item_brand: productData.brand,
              price: productData.price,
              quantity: 1
            }]
          });
        }
      });
    });
  }

  trackRemoveFromCart() {
    // Track remove from cart events
    const removeButtons = document.querySelectorAll('[data-remove-from-cart], .remove-from-cart');
    removeButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        const productData = this.extractProductData();
        if (productData) {
          gtag('event', 'remove_from_cart', {
            currency: 'GBP',
            value: productData.price,
            items: [{
              item_id: productData.id,
              item_name: productData.name,
              item_category: productData.category,
              item_brand: productData.brand,
              price: productData.price,
              quantity: 1
            }]
          });
        }
      });
    });
  }

  trackCheckout() {
    // Track checkout events
    const checkoutButtons = document.querySelectorAll('[data-checkout], .checkout, .buy-now');
    checkoutButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        const cartData = this.extractCartData();
        if (cartData) {
          gtag('event', 'begin_checkout', {
            currency: 'GBP',
            value: cartData.total,
            items: cartData.items
          });
        }
      });
    });
  }

  trackPurchases() {
    // Track purchase events
    if (window.location.pathname.includes('/thank-you') ||
      window.location.pathname.includes('/order-confirmation')) {
      const orderData = this.extractOrderData();
      if (orderData) {
        gtag('event', 'purchase', {
          transaction_id: orderData.transactionId,
          currency: 'GBP',
          value: orderData.total,
          items: orderData.items
        });

        // Mark user as customer
        localStorage.setItem('amizek_purchased', 'true');
      }
    }
  }

  trackRefunds() {
    // Track refund events (if applicable)
    if (window.location.pathname.includes('/refund') ||
      window.location.pathname.includes('/return')) {
      const refundData = this.extractRefundData();
      if (refundData) {
        gtag('event', 'refund', {
          transaction_id: refundData.transactionId,
          currency: 'GBP',
          value: refundData.amount
        });
      }
    }
  }

  extractProductData() {
    // Extract product data from page
    const productData = {
      id: this.getProductId(),
      name: this.getProductName(),
      category: this.getProductCategory(),
      brand: this.getProductBrand(),
      price: this.getProductPrice()
    };

    return productData.id ? productData : null;
  }

  getProductId() {
    // Get product ID from various sources
    const productId = document.querySelector('[data-product-id]')?.dataset.productId ||
      document.querySelector('.product-id')?.textContent?.trim() ||
      window.location.pathname.split('/products/')[1]?.split('?')[0];
    return productId;
  }

  getProductName() {
    // Get product name
    const productName = document.querySelector('h1')?.textContent?.trim() ||
      document.querySelector('.product-title')?.textContent?.trim() ||
      document.title.split(' | ')[0];
    return productName;
  }

  getProductCategory() {
    // Get product category
    const category = document.querySelector('[data-category]')?.dataset.category ||
      document.querySelector('.product-category')?.textContent?.trim() ||
      'General';
    return category;
  }

  getProductBrand() {
    // Get product brand
    const brand = document.querySelector('[data-brand]')?.dataset.brand ||
      document.querySelector('.product-brand')?.textContent?.trim() ||
      'Amizek';
    return brand;
  }

  getProductPrice() {
    // Get product price
    const priceElement = document.querySelector('.price, .product-price, [data-price]');
    if (priceElement) {
      const priceText = priceElement.textContent || priceElement.dataset.price;
      const price = parseFloat(priceText.replace(/[£$€,]/g, ''));
      return isNaN(price) ? 0 : price;
    }
    return 0;
  }

  extractCartData() {
    // Extract cart data
    const cartItems = document.querySelectorAll('.cart-item, .line-item');
    const items = [];
    let total = 0;

    cartItems.forEach(item => {
      const itemData = {
        item_id: item.dataset.productId || 'unknown',
        item_name: item.querySelector('.item-name')?.textContent?.trim() || 'Unknown Product',
        item_category: item.dataset.category || 'General',
        item_brand: item.dataset.brand || 'Amizek',
        price: parseFloat(item.dataset.price || '0'),
        quantity: parseInt(item.dataset.quantity || '1')
      };
      items.push(itemData);
      total += itemData.price * itemData.quantity;
    });

    return { items, total };
  }

  extractOrderData() {
    // Extract order data from confirmation page
    const orderData = {
      transactionId: this.getOrderId(),
      total: this.getOrderTotal(),
      items: this.getOrderItems()
    };

    return orderData.transactionId ? orderData : null;
  }

  getOrderId() {
    // Get order ID from URL or page
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('order_id') ||
      document.querySelector('[data-order-id]')?.dataset.orderId ||
      'unknown';
  }

  getOrderTotal() {
    // Get order total
    const totalElement = document.querySelector('.order-total, .total-amount, [data-total]');
    if (totalElement) {
      const totalText = totalElement.textContent || totalElement.dataset.total;
      return parseFloat(totalText.replace(/[£$€,]/g, ''));
    }
    return 0;
  }

  getOrderItems() {
    // Get order items
    const orderItems = document.querySelectorAll('.order-item, .purchased-item');
    const items = [];

    orderItems.forEach(item => {
      const itemData = {
        item_id: item.dataset.productId || 'unknown',
        item_name: item.querySelector('.item-name')?.textContent?.trim() || 'Unknown Product',
        item_category: item.dataset.category || 'General',
        item_brand: item.dataset.brand || 'Amizek',
        price: parseFloat(item.dataset.price || '0'),
        quantity: parseInt(item.dataset.quantity || '1')
      };
      items.push(itemData);
    });

    return items;
  }

  extractRefundData() {
    // Extract refund data
    return {
      transactionId: this.getOrderId(),
      amount: this.getOrderTotal()
    };
  }

  setupCustomEvents() {
    // Set up custom event tracking
    this.trackSearchEvents();
    this.trackNavigationEvents();
    this.trackEngagementEvents();
    this.trackErrorEvents();
  }

  trackSearchEvents() {
    // Track search events
    const searchInputs = document.querySelectorAll('input[type="search"], .search-input');
    searchInputs.forEach(input => {
      let searchTimeout;
      input.addEventListener('input', (e) => {
        clearTimeout(searchTimeout);
        searchTimeout = setTimeout(() => {
          const query = e.target.value.trim();
          if (query.length > 2) {
            gtag('event', 'search', {
              search_term: query
            });
          }
        }, 500);
      });
    });

    // Track search result clicks
    const searchResults = document.querySelectorAll('.search-result, .search-suggestion');
    searchResults.forEach(result => {
      result.addEventListener('click', () => {
        gtag('event', 'search_result_click', {
          search_term: result.dataset.query || 'unknown',
          result_position: result.dataset.position || 'unknown'
        });
      });
    });
  }

  trackNavigationEvents() {
    // Track navigation events
    const navLinks = document.querySelectorAll('.nav-link, .menu-link, nav a');
    navLinks.forEach(link => {
      link.addEventListener('click', () => {
        gtag('event', 'navigation_click', {
          link_text: link.textContent.trim(),
          link_url: link.href,
          page_location: window.location.href
        });
      });
    });

    // Track breadcrumb clicks
    const breadcrumbLinks = document.querySelectorAll('.breadcrumb a');
    breadcrumbLinks.forEach(link => {
      link.addEventListener('click', () => {
        gtag('event', 'breadcrumb_click', {
          breadcrumb_text: link.textContent.trim(),
          breadcrumb_url: link.href
        });
      });
    });
  }

  trackEngagementEvents() {
    // Track engagement events
    this.trackScrollDepth();
    this.trackTimeOnPage();
    this.trackVideoEngagement();
    this.trackImageEngagement();
  }

  trackScrollDepth() {
    // Track scroll depth
    let maxScrollDepth = 0;
    const scrollThresholds = [25, 50, 75, 90, 100];
    const triggeredThresholds = new Set();

    window.addEventListener('scroll', () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const documentHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollDepth = Math.round((scrollTop / documentHeight) * 100);

      if (scrollDepth > maxScrollDepth) {
        maxScrollDepth = scrollDepth;
      }

      scrollThresholds.forEach(threshold => {
        if (scrollDepth >= threshold && !triggeredThresholds.has(threshold)) {
          triggeredThresholds.add(threshold);
          gtag('event', 'scroll', {
            scroll_depth: threshold
          });
        }
      });
    });
  }

  trackTimeOnPage() {
    // Track time on page
    const startTime = Date.now();
    let timeIntervals = [30, 60, 120, 300, 600]; // 30s, 1m, 2m, 5m, 10m
    let triggeredIntervals = new Set();

    setInterval(() => {
      const timeOnPage = Math.round((Date.now() - startTime) / 1000);

      timeIntervals.forEach(interval => {
        if (timeOnPage >= interval && !triggeredIntervals.has(interval)) {
          triggeredIntervals.add(interval);
          gtag('event', 'time_on_page', {
            time_seconds: interval
          });
        }
      });
    }, 10000); // Check every 10 seconds
  }

  trackVideoEngagement() {
    // Track video engagement
    const videos = document.querySelectorAll('video');
    videos.forEach(video => {
      video.addEventListener('play', () => {
        gtag('event', 'video_play', {
          video_title: video.dataset.title || 'Unknown Video',
          video_duration: video.duration || 0
        });
      });

      video.addEventListener('pause', () => {
        gtag('event', 'video_pause', {
          video_title: video.dataset.title || 'Unknown Video',
          video_current_time: video.currentTime || 0
        });
      });

      video.addEventListener('ended', () => {
        gtag('event', 'video_complete', {
          video_title: video.dataset.title || 'Unknown Video',
          video_duration: video.duration || 0
        });
      });
    });
  }

  trackImageEngagement() {
    // Track image engagement
    const images = document.querySelectorAll('img');
    images.forEach(img => {
      img.addEventListener('click', () => {
        gtag('event', 'image_click', {
          image_src: img.src,
          image_alt: img.alt || 'No alt text'
        });
      });
    });
  }

  trackErrorEvents() {
    // Track JavaScript errors
    window.addEventListener('error', (e) => {
      gtag('event', 'javascript_error', {
        error_message: e.message,
        error_filename: e.filename,
        error_lineno: e.lineno,
        error_colno: e.colno
      });
    });

    // Track unhandled promise rejections
    window.addEventListener('unhandledrejection', (e) => {
      gtag('event', 'promise_rejection', {
        error_reason: e.reason?.toString() || 'Unknown error'
      });
    });

    // Track 404 errors
    if (document.title.includes('404') || document.body.textContent.includes('Page not found')) {
      gtag('event', 'page_not_found', {
        page_url: window.location.href
      });
    }
  }

  setupPerformanceMonitoring() {
    // Set up performance monitoring
    this.trackCoreWebVitals();
    this.trackResourceTiming();
    this.trackNavigationTiming();
  }

  trackCoreWebVitals() {
    // Track Core Web Vitals
    if ('PerformanceObserver' in window) {
      // Largest Contentful Paint
      const lcpObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const lastEntry = entries[entries.length - 1];
        gtag('event', 'core_web_vitals', {
          metric_name: 'LCP',
          metric_value: Math.round(lastEntry.startTime),
          metric_rating: lastEntry.startTime <= 2500 ? 'good' : lastEntry.startTime <= 4000 ? 'needs_improvement' : 'poor'
        });
      });
      lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });

      // First Input Delay
      const fidObserver = new PerformanceObserver((list) => {
        list.getEntries().forEach((entry) => {
          const fid = entry.processingStart - entry.startTime;
          gtag('event', 'core_web_vitals', {
            metric_name: 'FID',
            metric_value: Math.round(fid),
            metric_rating: fid <= 100 ? 'good' : fid <= 300 ? 'needs_improvement' : 'poor'
          });
        });
      });
      fidObserver.observe({ entryTypes: ['first-input'] });

      // Cumulative Layout Shift
      let clsValue = 0;
      const clsObserver = new PerformanceObserver((list) => {
        list.getEntries().forEach((entry) => {
          if (!entry.hadRecentInput) {
            clsValue += entry.value;
          }
        });
        gtag('event', 'core_web_vitals', {
          metric_name: 'CLS',
          metric_value: Math.round(clsValue * 1000) / 1000,
          metric_rating: clsValue <= 0.1 ? 'good' : clsValue <= 0.25 ? 'needs_improvement' : 'poor'
        });
      });
      clsObserver.observe({ entryTypes: ['layout-shift'] });
    }
  }

  trackResourceTiming() {
    // Track resource timing
    if ('PerformanceObserver' in window) {
      const resourceObserver = new PerformanceObserver((list) => {
        list.getEntries().forEach((entry) => {
          if (entry.duration > 1000) { // Only track slow resources
            gtag('event', 'slow_resource', {
              resource_name: entry.name,
              resource_duration: Math.round(entry.duration),
              resource_type: entry.initiatorType
            });
          }
        });
      });
      resourceObserver.observe({ entryTypes: ['resource'] });
    }
  }

  trackNavigationTiming() {
    // Track navigation timing
    window.addEventListener('load', () => {
      setTimeout(() => {
        const navigation = performance.getEntriesByType('navigation')[0];
        if (navigation) {
          gtag('event', 'page_timing', {
            page_load_time: Math.round(navigation.loadEventEnd - navigation.loadEventStart),
            dom_content_loaded: Math.round(navigation.domContentLoadedEventEnd - navigation.domContentLoadedEventStart),
            first_byte: Math.round(navigation.responseStart - navigation.requestStart)
          });
        }
      }, 1000);
    });
  }

  setupUserBehaviorTracking() {
    // Set up user behavior tracking
    this.trackUserJourney();
    this.trackSessionData();
    this.trackDeviceInfo();
  }

  trackUserJourney() {
    // Track user journey
    const journey = JSON.parse(localStorage.getItem('amizek_user_journey') || '[]');
    const currentPage = {
      url: window.location.href,
      title: document.title,
      timestamp: Date.now(),
      referrer: document.referrer
    };

    journey.push(currentPage);

    // Keep only last 10 pages
    if (journey.length > 10) {
      journey.shift();
    }

    localStorage.setItem('amizek_user_journey', JSON.stringify(journey));

    // Track page view with journey context
    gtag('event', 'page_view', {
      page_title: document.title,
      page_location: window.location.href,
      journey_length: journey.length,
      previous_page: journey.length > 1 ? journey[journey.length - 2].url : null
    });
  }

  trackSessionData() {
    // Track session data
    const sessionId = this.getOrCreateSessionId();
    const sessionStart = localStorage.getItem('amizek_session_start') || Date.now();
    const pageViews = parseInt(localStorage.getItem('amizek_page_views') || '0') + 1;

    localStorage.setItem('amizek_session_start', sessionStart);
    localStorage.setItem('amizek_page_views', pageViews.toString());

    gtag('event', 'session_data', {
      session_id: sessionId,
      session_duration: Date.now() - sessionStart,
      page_views: pageViews
    });
  }

  getOrCreateSessionId() {
    let sessionId = localStorage.getItem('amizek_session_id');
    if (!sessionId) {
      sessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      localStorage.setItem('amizek_session_id', sessionId);
    }
    return sessionId;
  }

  trackDeviceInfo() {
    // Track device and browser info
    const deviceInfo = {
      user_agent: navigator.userAgent,
      screen_resolution: `${screen.width}x${screen.height}`,
      viewport_size: `${window.innerWidth}x${window.innerHeight}`,
      color_depth: screen.colorDepth,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      language: navigator.language,
      platform: navigator.platform,
      cookie_enabled: navigator.cookieEnabled,
      online_status: navigator.onLine
    };

    gtag('event', 'device_info', deviceInfo);
  }

  setupConversionTracking() {
    // Set up conversion tracking
    this.trackFormSubmissions();
    this.trackNewsletterSignups();
    this.trackContactFormSubmissions();
    this.trackSocialMediaClicks();
  }

  trackFormSubmissions() {
    // Track form submissions
    const forms = document.querySelectorAll('form');
    forms.forEach(form => {
      form.addEventListener('submit', (e) => {
        const formData = new FormData(form);
        const formFields = {};

        for (let [key, value] of formData.entries()) {
          formFields[key] = value;
        }

        gtag('event', 'form_submit', {
          form_id: form.id || 'unknown',
          form_class: form.className || 'unknown',
          form_fields: Object.keys(formFields).length
        });
      });
    });
  }

  trackNewsletterSignups() {
    // Track newsletter signups
    const newsletterForms = document.querySelectorAll('.newsletter-form, [data-newsletter]');
    newsletterForms.forEach(form => {
      form.addEventListener('submit', (e) => {
        gtag('event', 'newsletter_signup', {
          form_location: form.dataset.location || 'unknown'
        });
      });
    });
  }

  trackContactFormSubmissions() {
    // Track contact form submissions
    const contactForms = document.querySelectorAll('.contact-form, [data-contact]');
    contactForms.forEach(form => {
      form.addEventListener('submit', (e) => {
        gtag('event', 'contact_form_submit', {
          form_type: form.dataset.type || 'general'
        });
      });
    });
  }

  trackSocialMediaClicks() {
    // Track social media clicks
    const socialLinks = document.querySelectorAll('a[href*="facebook"], a[href*="twitter"], a[href*="instagram"], a[href*="linkedin"]');
    socialLinks.forEach(link => {
      link.addEventListener('click', () => {
        const platform = this.getSocialPlatform(link.href);
        gtag('event', 'social_media_click', {
          social_platform: platform,
          link_url: link.href
        });
      });
    });
  }

  getSocialPlatform(url) {
    if (url.includes('facebook')) return 'facebook';
    if (url.includes('twitter')) return 'twitter';
    if (url.includes('instagram')) return 'instagram';
    if (url.includes('linkedin')) return 'linkedin';
    return 'other';
  }

  setupRealTimeMonitoring() {
    // Set up real-time monitoring
    this.createAnalyticsDashboard();
    this.setupAlerts();
    this.monitorKeyMetrics();
  }

  createAnalyticsDashboard() {
    // Create real-time analytics dashboard
    if (this.config.debugMode || window.amizekAdminEnabled) {
      const dashboard = document.createElement('div');
      dashboard.id = 'amizek-analytics-dashboard';
      dashboard.style.cssText = `
        position: fixed;
        bottom: 20px;
        right: 20px;
        width: 300px;
        background: white;
        border: 1px solid #ddd;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        z-index: 10000;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        font-size: 12px;
        max-height: 400px;
        overflow-y: auto;
      `;

      dashboard.innerHTML = `
        <div style="background: #ff6b35; color: white; padding: 10px; border-radius: 8px 8px 0 0;">
          <h4 style="margin: 0; font-size: 14px;">Analytics Dashboard</h4>
          <button onclick="document.getElementById('amizek-analytics-dashboard').remove()" 
                  style="float: right; background: none; border: none; color: white; font-size: 16px; cursor: pointer;">×</button>
        </div>
        <div style="padding: 10px;">
          <div id="analytics-metrics"></div>
        </div>
      `;

      document.body.appendChild(dashboard);
      this.updateDashboard();
    }
  }

  updateDashboard() {
    // Update analytics dashboard
    const metricsContainer = document.getElementById('analytics-metrics');
    if (metricsContainer) {
      const metrics = {
        'Page Views': localStorage.getItem('amizek_page_views') || '0',
        'Session Duration': Math.round((Date.now() - parseInt(localStorage.getItem('amizek_session_start') || Date.now())) / 1000) + 's',
        'User Type': this.detectUserType(),
        'Device': navigator.platform,
        'Online': navigator.onLine ? 'Yes' : 'No'
      };

      metricsContainer.innerHTML = Object.entries(metrics).map(([key, value]) => `
        <div style="display: flex; justify-content: space-between; margin-bottom: 5px;">
          <span>${key}:</span>
          <span style="font-weight: bold;">${value}</span>
        </div>
      `).join('');
    }
  }

  setupAlerts() {
    // Set up performance alerts
    this.alertOnSlowPages();
    this.alertOnHighBounceRate();
    this.alertOnErrors();
  }

  alertOnSlowPages() {
    // Alert on slow page loads
    window.addEventListener('load', () => {
      setTimeout(() => {
        const navigation = performance.getEntriesByType('navigation')[0];
        if (navigation) {
          const loadTime = navigation.loadEventEnd - navigation.loadEventStart;
          if (loadTime > 3000) {
            gtag('event', 'slow_page_alert', {
              page_url: window.location.href,
              load_time: Math.round(loadTime)
            });
          }
        }
      }, 1000);
    });
  }

  alertOnHighBounceRate() {
    // Alert on potential high bounce rate
    let timeOnPage = 0;
    setInterval(() => {
      timeOnPage += 10;
      if (timeOnPage === 10) { // 10 seconds
        gtag('event', 'potential_bounce', {
          page_url: window.location.href,
          time_on_page: timeOnPage
        });
      }
    }, 10000);
  }

  alertOnErrors() {
    // Alert on errors
    let errorCount = 0;
    window.addEventListener('error', () => {
      errorCount++;
      if (errorCount >= 3) {
        gtag('event', 'high_error_rate', {
          page_url: window.location.href,
          error_count: errorCount
        });
      }
    });
  }

  monitorKeyMetrics() {
    // Monitor key business metrics
    this.monitorConversionRate();
    this.monitorCartAbandonment();
    this.monitorUserEngagement();
  }

  monitorConversionRate() {
    // Monitor conversion rate
    const pageViews = parseInt(localStorage.getItem('amizek_page_views') || '0');
    const purchases = localStorage.getItem('amizek_purchased') === 'true' ? 1 : 0;
    const conversionRate = pageViews > 0 ? (purchases / pageViews) * 100 : 0;

    gtag('event', 'conversion_rate', {
      page_views: pageViews,
      purchases: purchases,
      conversion_rate: Math.round(conversionRate * 100) / 100
    });
  }

  monitorCartAbandonment() {
    // Monitor cart abandonment
    const addToCartEvents = parseInt(localStorage.getItem('amizek_add_to_cart') || '0');
    const checkoutEvents = parseInt(localStorage.getItem('amizek_checkout') || '0');
    const abandonmentRate = addToCartEvents > 0 ? ((addToCartEvents - checkoutEvents) / addToCartEvents) * 100 : 0;

    gtag('event', 'cart_abandonment', {
      add_to_cart: addToCartEvents,
      checkout: checkoutEvents,
      abandonment_rate: Math.round(abandonmentRate * 100) / 100
    });
  }

  monitorUserEngagement() {
    // Monitor user engagement
    const scrollDepth = parseInt(localStorage.getItem('amizek_max_scroll_depth') || '0');
    const timeOnPage = parseInt(localStorage.getItem('amizek_time_on_page') || '0');

    gtag('event', 'user_engagement', {
      scroll_depth: scrollDepth,
      time_on_page: timeOnPage,
      engagement_score: Math.round((scrollDepth + timeOnPage) / 2)
    });
  }

  // Utility methods
  trackCustomEvent(eventName, parameters = {}) {
    gtag('event', eventName, parameters);
  }

  setUserProperty(property, value) {
    gtag('config', this.config.measurementId, {
      user_properties: {
        [property]: value
      }
    });
  }

  setUserId(userId) {
    gtag('config', this.config.measurementId, {
      user_id: userId
    });
  }
}

// Initialize analytics when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new AmizekAnalytics();
});

// Export for use in other modules
window.AmizekAnalytics = AmizekAnalytics;
